﻿import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles, IconButton, Tooltip } from "@material-ui/core";
import VerticalSplitOutlinedIcon from "@material-ui/icons/VerticalSplitOutlined";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    flex: 1,
    minWidth: 0,
    maxWidth: "none",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
    },
  },
  ticketActionButtons: {
    flex: "none",
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: 4,
      marginLeft: 0,
      justifyContent: "flex-end"
    },
  },
  contextToggle: {
    marginLeft: 6,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    "&:hover": {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  wabaWindowBar: {
    width: "100%",
    padding: "8px 14px",
    fontSize: "0.82rem",
    fontWeight: 600,
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  wabaWindowBarActive: {
    backgroundColor: "#FFF1F0",
    color: "#8A1C1C",
  },
  wabaWindowBarExpired: {
    backgroundColor: "#FFF1F0",
    color: "#8A1C1C",
  },
  peekBar: {
    width: "100%",
    padding: "8px 14px",
    fontSize: "0.82rem",
    fontWeight: 700,
    borderTop: "1px solid rgba(29, 78, 216, 0.22)",
    borderBottom: "1px solid rgba(29, 78, 216, 0.22)",
    backgroundColor: "#EFF6FF",
    color: "#1E40AF",
  },

  noQueueBar: {
    width: "100%",
    padding: "8px 14px",
    fontSize: "0.82rem",
    fontWeight: 700,
    borderTop: "1px solid rgba(180, 83, 9, 0.22)",
    borderBottom: "1px solid rgba(180, 83, 9, 0.22)",
    backgroundColor: "#FFF7ED",
    color: "#92400E",
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [createdMessagesBatch, setCreatedMessagesBatch] = useState(null);
  const { user } = useContext(AuthContext);
  const internalTicketId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("internalTicketId");
  }, [location.search]);
  // Modo espiar vem pela URL de proposito: assim o estado sobrevive a um
  // recarregamento e a um link colado para um colega, em vez de existir so na
  // memoria de quem clicou no olho.
  const isPeekMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("peek") === "1";
  }, [location.search]);

  const fetchTicket = async () => {
    try {
      const { data } = await api.get("/tickets/" + ticketId, {
        params: internalTicketId ? { internalTicketId } : undefined
      });

      setContact(data.contact);
      setTicket(data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      const statusCode = Number(err?.response?.status || 0);
      const backendError = String(err?.response?.data?.error || "");
      if (statusCode === 404 || backendError === "ERR_NO_TICKET_FOUND") {
        history.push("/tickets");
        return;
      }
      toastError(err);
    }
  };

  useEffect(() => {
    setLoading(true);
    setContact({});
    setTicket({});
    setCreatedMessagesBatch(null);
    fetchTicket();
  }, [ticketId, history, internalTicketId]);

  useEffect(() => {
    const socket = openSocket();
    const joinCurrentChat = () =>
      socket.emit(
        "joinChatBox",
        internalTicketId ? { ticketId, internalTicketId } : ticketId
      );
    const currentTicketMatches = data => {
      const eventTicketId = Number(
        data?.ticket?.id ||
          data?.ticketId ||
          data?.message?.ticketId ||
          data?.message?.ticket?.id ||
          0
      );
      return eventTicketId && eventTicketId === Number(ticketId || 0);
    };

    socket.on("connect", joinCurrentChat);
    joinCurrentChat();

    socket.on("ticket", (data) => {
      if (!currentTicketMatches(data)) {
        return;
      }

      if (data.action === "update") {
        setTicket((prevState) => ({
          ...prevState,
          ...data.ticket,
        }));
        fetchTicket();
      }

      if (data.action === "delete") {
        // O cliente abriu o link do WebChat: este chamado do WhatsApp fechou e a
        // conversa segue no novo. O destino vem no proprio evento de fechamento,
        // que e o primeiro a chegar - um evento separado encontraria esta tela
        // ja desmontada pelo push abaixo.
        if (data.handoffTicketId) {
          toast.info("O cliente abriu o link: a conversa continua no WebChat.");
          history.push(`/tickets/${data.handoffTicketId}`);
          return;
        }
        if (data.reason === "ticket-deleted") {
          toast.success("Ticket excluido com sucesso.");
        }
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    socket.on("appMessage", data => {
      if (!currentTicketMatches(data) || !data?.message) {
        return;
      }

      setCreatedMessagesBatch({
        id: `${Date.now()}:${data.message.id || Math.random()}`,
        messages: [data.message]
      });
    });

    socket.on("wabaCall", data => {
      if (Number(data?.ticketId) === Number(ticketId)) {
        fetchTicket();
      }
    });

    socket.on("wabaCallControl", data => {
      if (Number(data?.ticketId) === Number(ticketId)) {
        fetchTicket();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history, internalTicketId]);

  useEffect(() => {
    const handleUpdatedTicket = event => {
      const updatedTicket = event?.detail?.ticket;
      const deletedTicketId = Number(event?.detail?.deletedTicketId || 0);
      const currentTicketId = Number(ticketId || 0);

      if (deletedTicketId && deletedTicketId === currentTicketId) {
        history.push("/tickets");
        return;
      }

      if (!updatedTicket || Number(updatedTicket.id || 0) !== currentTicketId) {
        return;
      }

      setTicket(prevState => {
        const previousUserMatches =
          prevState.user &&
          Number(prevState.user.id || 0) ===
            Number(updatedTicket.userId || prevState.userId || 0);

        return {
          ...prevState,
          ...updatedTicket,
          user: updatedTicket.user || (previousUserMatches ? prevState.user : null)
        };
      });

      if (updatedTicket.contact) {
        setContact(prevState => ({
          ...prevState,
          ...updatedTicket.contact
        }));
      }
    };

    window.addEventListener("ticket:updated", handleUpdatedTicket);

    return () => {
      window.removeEventListener("ticket:updated", handleUpdatedTicket);
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const canWriteToTicket =
    ticket.status === "open" &&
    (!ticket.userId || Number(ticket.userId) === Number(user?.id));
  const isAdminLikeUser =
    user?.profile === "admin" ||
    user?.profile === "superadmin" ||
    Boolean(user?.supportSession?.active) ||
    Boolean(user?.supervisorMode);
  const canCommentOnTicket =
    ticket.status === "open" &&
    (isAdminLikeUser ||
      (ticket.userId && Number(ticket.userId) === Number(user?.id)));
  const canEditSubject =
    ticket.status === "open" &&
    (isAdminLikeUser ||
      !ticket.userId ||
      Number(ticket.userId) === Number(user?.id));

  const isReadOnlyInternalView =
    Boolean(internalTicketId) &&
    !isAdminLikeUser &&
    !canWriteToTicket;
  const ticketHasNoQueue =
    !loading &&
    Boolean(ticket?.id) &&
    ticket.status !== "closed" &&
    !ticket.queueId &&
    !ticket.queue?.id;

  const isWabaTicket =
    String(ticket?.whatsapp?.connectionType || "").toLowerCase() === "waba" &&
    Boolean(ticket?.whatsapp?.wabaEnabled);
  const isPlatformInitiatedWabaConversation = Boolean(
    ticket?.wabaConversationInitiatedByPlatform
  );
  const wabaTemplateRequired = Boolean(ticket?.wabaTemplateRequired);
  const wabaWindowExpiresAt = ticket?.wabaWindowExpiresAt
    ? new Date(ticket.wabaWindowExpiresAt)
    : null;
  const isTicketReady =
    !loading && Number(ticket?.id || 0) === Number(ticketId || 0);
  const formatDateTime = value => {
    if (!value || Number.isNaN(value.getTime())) return "-";
    return value.toLocaleString("pt-BR");
  };
  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          {isTicketReady ? (
            <>
              <div className={classes.ticketInfo}>
                <TicketInfo
                  contact={contact}
                  ticket={ticket}
                  onClick={handleDrawerOpen}
                  canEditSubject={canEditSubject}
                  subjectRequired={Boolean(ticket.subjectRequired)}
                />
              </div>
              <div className={classes.ticketActionButtons}>
                <TicketActionButtons
                  ticket={ticket}
                  onRefreshTicket={fetchTicket}
                  readOnly={isReadOnlyInternalView}
                />
                <Tooltip
                  arrow
                  title={drawerOpen ? "Ocultar detalhes" : "Mostrar detalhes"}
                >
                  <IconButton
                    size="small"
                    className={classes.contextToggle}
                    onClick={() =>
                      drawerOpen ? handleDrawerClose() : handleDrawerOpen()
                    }
                  >
                    <VerticalSplitOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </>
          ) : null}
        </TicketHeader>
        {isTicketReady && isPeekMode && (
          <div className={classes.peekBar}>
            Modo espiar: as mensagens continuam marcadas como não lidas. Aceite o
            chamado ou responda para marcá-las como lidas.
          </div>
        )}
        {isTicketReady && ticketHasNoQueue && (
          <div className={classes.noQueueBar}>
            Ticket sem fila. Selecione uma fila para que a equipe consiga acompanhar este atendimento.
          </div>
        )}
        {isTicketReady && isWabaTicket && (
          <div
            className={clsx(classes.wabaWindowBar, {
              [classes.wabaWindowBarActive]: !wabaTemplateRequired,
              [classes.wabaWindowBarExpired]: wabaTemplateRequired,
            })}
          >
            {wabaTemplateRequired
              ? isPlatformInitiatedWabaConversation
                ? "Janela WABA de 24h expirada. Para continuar, envie template WABA."
                : "Janela WABA de 24h indisponivel. Para iniciar conversa, envie template WABA."
            : `Janela de 24h ativa até ${formatDateTime(wabaWindowExpiresAt)}.`}
          </div>
        )}
        {isTicketReady ? (
          <ReplyMessageProvider>
            <MessagesList
              ticketId={ticketId}
              ticket={ticket}
              isGroup={ticket.isGroup}
              internalTicketId={internalTicketId}
              canInteract={canWriteToTicket}
              createdMessagesBatch={createdMessagesBatch}
              peek={isPeekMode}
            ></MessagesList>
            <MessageInput
              ticketStatus={ticket.status}
              canWrite={canWriteToTicket}
              canComment={canCommentOnTicket}
              ticket={ticket}
              ticketSubject={ticket.subject}
              subjectRequired={Boolean(ticket.subjectRequired)}
              isGroup={Boolean(ticket.isGroup)}
              ticketWhatsapp={ticket.whatsapp}
              wabaTemplateRequired={wabaTemplateRequired}
              onMessagesCreated={messages => {
                setCreatedMessagesBatch({
                  id: `${Date.now()}:${Math.random()}`,
                  messages
                });
              }}
            />
          </ReplyMessageProvider>
        ) : null}
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        loading={loading}
        currentTicketId={ticket?.id}
        ticketHistory={ticket?.ticketHistory || []}
        onRefreshTicket={fetchTicket}
      />
    </div>
  );
};

export default Ticket;


